import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AdminShell, adminPage } from '#/components/admin/admin-shell'
import { DataTable, Pager, StatTile } from '#/components/admin/ui'
import { Badge } from '#/components/ui/badge'
import { useAdminCampusLeads } from '#/lib/api-admin'
import type { AdminCampusLead } from '#/lib/api-admin'
import { fmtDateTime, fmtNumber } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/admin/campus')({
  component: adminPage(Page),
})

function Page() {
  const [page, setPage] = useState(1)
  const { data, isLoading } = useAdminCampusLeads(page)
  const by = (k: string) =>
    data?.byPartnership.find((b) => b.partnership === k)?.n ?? 0
  return (
    <AdminShell
      title={m.admin_nav_campus()}
      description={m.admin_campus_desc()}
      wide
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label={m.admin_campus_total()}
          value={data ? fmtNumber(data.total) : '…'}
          sub={
            data
              ? `kelas ${by('kelas')} · komunitas ${by('komunitas')} · kampus ${by('kampus')} · perorangan ${by('perorangan')} · lead ${by('lead')}`
              : null
          }
        />
        <StatTile
          label={m.admin_campus_teams()}
          value={data ? fmtNumber(data.teams) : '…'}
        />
      </div>
      <DataTable<AdminCampusLead>
        rows={data?.leads}
        loading={isLoading}
        rowKey={(l) => l.id}
        cols={[
          {
            key: 'date',
            header: m.tx_col_date(),
            render: (l) => fmtDateTime(l.createdAt),
          },
          {
            key: 'inst',
            header: m.admin_col_institution(),
            render: (l) => <span className="font-medium">{l.institution}</span>,
          },
          {
            key: 'who',
            header: m.admin_col_email(),
            render: (l) => (
              <span className="block">
                <span>{l.name ?? '—'}</span>
                <span className="block text-xs text-muted-foreground">
                  {l.email}
                  {l.phone ? ` · ${l.phone}` : ''}
                </span>
              </span>
            ),
          },
          {
            key: 'partnership',
            header: m.admin_col_partnership(),
            render: (l) =>
              l.partnership ? (
                <Badge variant="outline">{l.partnership}</Badge>
              ) : (
                '—'
              ),
          },
          {
            key: 'role',
            header: m.admin_col_role(),
            render: (l) => l.role ?? '—',
          },
          {
            key: 'track',
            header: m.admin_col_track(),
            render: (l) => l.track ?? '—',
          },
          {
            key: 'teams',
            header: m.admin_col_teams(),
            render: (l) => (l.teams == null ? '—' : fmtNumber(l.teams)),
          },
          {
            key: 'start',
            header: m.admin_col_start(),
            render: (l) => l.startTerm ?? '—',
          },
          {
            key: 'note',
            header: m.admin_col_note(),
            render: (l) => (
              <span className="block max-w-md whitespace-pre-wrap text-xs">
                {l.note ?? '—'}
              </span>
            ),
          },
        ]}
      />
      {data ? (
        <Pager
          page={data.page}
          total={data.total}
          pageSize={data.pageSize}
          onPage={setPage}
        />
      ) : null}
    </AdminShell>
  )
}
